import MarketInsightBookingCurveFragment from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightBookingCurveFragment';
import React from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { useMarketInsightQuery } from '@app/model/market_insight/service/MarketInsightServiceHooks';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import MarketInsightMarketAreaStatesGraph
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightMarketAreaStatesFragment';
import Utilities from '@util/Utilities';
import MarketInsightMarketDemandFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightMarketDemandFragment';
import MarketInsightPriceIndexFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightPriceIndexFragment';
import MarketInsightSoldOutIndicesFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightSoldOutIndicesFragment';
import MarketInsightLosIndicesFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightLosIndicesFragment';
import MarketInsightOccupancyIndicesFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightOccupancyIndicesFragment';
import MarketInsightFlightStatsFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightFlightStatsFragment';
import MarketInsightOccupancyMapFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/MarketInsightOccupancyMapFragment';
import MarketInsight from '@app/model/market_insight/MarketInsight';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import { HomeIcon } from '@heroicons/react/20/solid';

const MarketInsightPage = React.memo(() => {

	return <main>
			<div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8 items-center">
				<MarketInsightPageFragment />
			</div>
		</main>
});

const MarketInsightPageFragment = React.memo(() => {

	const [skipDays, setSkipDays] = React.useState<number>(0);
	const [stateIndex, setStateIndex] = React.useState<number>(0);
	const { companyHotel } = useSelectedCompanyHotel();
	const { data: marketInsight, isLoading, isSuccess } = useMarketInsightQuery(companyHotel.id, skipDays);
	// The market tab (U3) lives here, above the placeholder of a new window of dates, so a change of date keeps it.
	// It belongs to the hotel that chose it: another hotel starts on its own market area.
	const [marketTab, setMarketTab] = React.useState<{ companyHotelId: number, marketAreaId: number } | null>(null);
	const chosenMarketAreaId = marketTab?.companyHotelId === companyHotel.id ? marketTab.marketAreaId : null;

	if (isLoading) return <TablePlaceholder />;

	if (isSuccess) {
		if (!marketInsight)
			return <Message state="warning">Market insight not found!</Message>;

		return <MarketInsightState marketInsight={marketInsight} skipDays={skipDays} onSetSkipDays={setSkipDays}
			stateIndex={stateIndex} onSelectStateIndex={setStateIndex}
			chosenMarketAreaId={chosenMarketAreaId}
			onChooseMarketArea={marketAreaId => setMarketTab({ companyHotelId: companyHotel.id, marketAreaId })} />;
	}

	return <Message state="error">Market insight not found!</Message>;

});

const MarketInsightState: React.FunctionComponent<{
	marketInsight: MarketInsight,
	skipDays: number,
	onSetSkipDays: (skipDays: number) => void,
	stateIndex: number,
	onSelectStateIndex: (index: number) => void,
	chosenMarketAreaId: number | null,
	onChooseMarketArea: (marketAreaId: number) => void,
}> = ({marketInsight, skipDays, onSetSkipDays, stateIndex, onSelectStateIndex, chosenMarketAreaId, onChooseMarketArea}) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const states = marketInsight.marketRegionStates ?? [];
	const selectedIndex = Math.min(Math.max(stateIndex, 0), states.length - 1);
	const selectedStatDate = states[selectedIndex]?.statDate ?? null;
	const [compareForDaysBefore] = React.useState<number | undefined>(14);

	// The market tabs: the market areas of the region of the hotel. The default tab is the market area of the hotel.
	const marketAreas = marketInsight.marketAreas ?? [];
	const ownMarketAreaId = marketAreas.find(area => area.own)?.id ?? null;
	// Only a market area of the current list; else the own market area.
	const selectedMarketAreaId = marketAreas.some(area => area.id === chosenMarketAreaId) ? chosenMarketAreaId : ownMarketAreaId;
	const selectedMarketArea = marketAreas.find(area => area.id === selectedMarketAreaId);
	const hasTabs = marketAreas.length > 1;
	const regionName = hasTabs ? marketInsight.marketRegionName : null;


	return <>
		<MarketInsightMarketAreaStatesGraph marketInsight={marketInsight}
																				stateIndex={selectedIndex}
																				onSelectStateIndex={onSelectStateIndex}
																				onSetSkipDays={onSetSkipDays}
																				skipDays={skipDays} />

		{selectedStatDate !== null && selectedStatDate !== '' && <div>
			<div className="mt-8 mb-4">
				<div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
					<h1
						className="text-base/7 font-bold text-gray-900">{Utilities.formatDateString(selectedStatDate, 'DD MMMM YYYY', false)}</h1>
					{hasTabs && <div className="w-full min-w-0 sm:w-auto">
						<ButtonTabs label={`Markets of the ${marketInsight.marketRegionName ?? ''} region`}
							value={String(selectedMarketAreaId)}
							onChange={value => onChooseMarketArea(Number(value))}
							options={marketAreas.map(area => ({
								value: String(area.id),
								label: area.name,
								icon: area.own ? <HomeIcon className="h-3.5 w-3.5" /> : undefined,
								note: area.own ? 'your market' : undefined,
							}))} />
					</div>}
				</div>
				{hasTabs && selectedMarketArea !== undefined && <p className="mt-2 text-xs text-gray-500 sm:text-right">
					<span className="font-semibold text-gray-700">{selectedMarketArea.name}</span>: Price Index, Occupancy, Sold Out, Length of Stay, Booking Curve and the map. <span
						className="font-semibold text-gray-700">{regionName} region</span>: Market Demand and Top Origin Markets.
				</p>}
			</div>
			<div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-9">
				<div className="grid grid-cols-1 lg:col-span-3">
					<section className="space-y-4">
						<MarketInsightMarketDemandFragment marketInsight={marketInsight} statDate={selectedStatDate} regionName={regionName} />
						<MarketInsightPriceIndexFragment statDate={selectedStatDate} marketAreaId={selectedMarketAreaId} />
						<MarketInsightOccupancyIndicesFragment statDate={selectedStatDate} marketAreaId={selectedMarketAreaId} />
						<MarketInsightSoldOutIndicesFragment statDate={selectedStatDate} marketAreaId={selectedMarketAreaId} />
						<MarketInsightLosIndicesFragment statDate={selectedStatDate} marketAreaId={selectedMarketAreaId} />
						<MarketInsightFlightStatsFragment statDate={selectedStatDate} compareForDaysBefore={compareForDaysBefore} regionName={regionName} />
					</section>
				</div>
				<div className="grid grid-cols-1 lg:col-span-6">
					<section className="space-y-4">
						{companyHotel.settings.main.enableMarketBookingCurve && <MarketInsightBookingCurveFragment statDate={selectedStatDate} marketAreaId={selectedMarketAreaId} />}
						<div className="rounded-lg bg-white p-2 shadow h-market-map">
							<MarketInsightOccupancyMapFragment statDate={selectedStatDate} marketAreaId={selectedMarketAreaId} />
						</div>
					</section>
				</div>
			</div>
		</div>}
	</>;
}

export default MarketInsightPage;
